/**
 * EXPERIMENTAL — DO NOT COPY.
 *
 * Machine enrollment and machine authentication for one internal account. This
 * code has not been through a full security review. It is safe only because
 * `machineFrontDoor.ts` (shared secret + source-IP allowlist) and the Statsig gate
 * run before any of it. Do not reuse its patterns — codes, credential hashing,
 * the confirmation flow, socket auth — for any other auth surface. Start from
 * the reviewed session auth in `api/src/security/` instead.
 */

import * as Sentry from "@sentry/react";
import { useQuery } from "@tanstack/react-query";
import { Copy, Laptop } from "lucide-react";
import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button.tsx";
import { Checkbox } from "@/components/ui/checkbox.tsx";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog.tsx";
import { Input } from "@/components/ui/input.tsx";
import { Label } from "@/components/ui/label.tsx";
import { errorToast } from "@/components/ui/toast.tsx";
import type { MachineRosterResp, MintConnectionCodeResp } from "@/types/userMachines.ts";
import {
  computerQueries,
  useConfirmMachine,
  useDisconnectMachine,
  useMintConnectionCode,
} from "./computersApi.ts";
import { SettingsPanelHeader } from "./SettingsPanelHeader.tsx";

type RosterEntry = MachineRosterResp["machines"][number];

/** How near expiry a connection gets called out rather than just dated. */
const EXPIRY_WARNING_DAYS = 7;

/**
 * What the user agrees to before any code exists.
 *
 * Each line is a consequence they can check against their own situation, which
 * is why they are consequences rather than a description of the feature. The
 * warning below them is the part that matters most, so it is not a footnote.
 */
const CONSEQUENCES = [
  "It acts as you. Commands run under your user account, so okthx can read, change and delete anything that account can.",
  "What it reads can end up in a conversation. Output comes back into the thread, which means it can be quoted in a reply, a summary, or a message you ask okthx to send.",
  "Commands inherit the shell you started it from. Any API keys or tokens in that environment are handed to every command. Start it from a clean shell.",
  "You can disconnect it at any time, from this page. Signing out of all devices disconnects it too.",
  "The connection lasts 30 days. After that the computer stops answering until you connect it again from this page.",
  "The computer keeps a key on disk. Anyone who can use that user account on that machine can use the key.",
];

const copy = async (value: string, label: string) => {
  try {
    await navigator.clipboard.writeText(value);
  } catch (error) {
    // The error, never the value: both things copied here are live secrets, and
    // a breadcrumb carrying one would put it somewhere nobody chose to put it.
    Sentry.captureException(error, { tags: { action: "settings-computers-copy" } });
    errorToast(`Couldn't copy the ${label}. Select it and copy manually.`);
  }
};

const relativeLastSeen = (iso: string | null): string => {
  if (iso === null) return "never";
  const minutes = Math.round((Date.now() - Date.parse(iso)) / 60_000);
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.round(hours / 24)}d ago`;
};

const expiryNote = (entry: RosterEntry): { text: string; warn: boolean } | null => {
  // Trust the server's verdict rather than re-deriving it here. Two clocks
  // answering one question let a browser a few minutes ahead label an active
  // machine "connection expired".
  if (entry.status === "expired") {
    // An expired machine with no `expiresAt` was never confirmed: its code ran
    // out before anyone typed it, so it needs connecting from scratch rather
    // than renewing.
    if (entry.expiresAt === null) return { text: "didn't finish connecting", warn: true };
    return { text: "connection expired", warn: true };
  }
  if (entry.expiresAt === null) return null;
  const days = Math.ceil((Date.parse(entry.expiresAt) - Date.now()) / 86_400_000);
  if (days <= 0) return { text: "expires today", warn: true };
  if (days <= EXPIRY_WARNING_DAYS) {
    return { text: `expires in ${days} day${days === 1 ? "" : "s"}`, warn: true };
  }
  return { text: `expires ${entry.expiresAt.slice(0, 10)}`, warn: false };
};

/** One machine in the list, with whatever it currently needs from the user. */
const ComputerRow = ({ entry }: { entry: RosterEntry }) => {
  const [code, setCode] = useState("");
  const confirm = useConfirmMachine();
  const disconnect = useDisconnectMachine();
  const [confirmingDisconnect, setConfirmingDisconnect] = useState(false);
  const expiry = expiryNote(entry);

  return (
    <div className="flex flex-col gap-3 border-b border-border py-4 last:border-b-0">
      <div className="flex items-center justify-between gap-3">
        <div className="flex min-w-0 items-center gap-2">
          <Laptop className="size-4 shrink-0 text-muted-foreground" />
          <span className="truncate font-medium">{entry.machineName}</span>
        </div>
        <div className="flex shrink-0 items-center gap-3 text-sm text-muted-foreground">
          {/* Last seen, not "online". The daemon registry is process-local and
              in production the socket service is a different process from the
              API, so a live indicator would be right here and wrong there. */}
          {entry.status === "active" && <span>last seen {relativeLastSeen(entry.lastSeenAt)}</span>}
          {expiry && (
            <span className={expiry.warn ? "text-amber-600 dark:text-amber-500" : undefined}>
              {expiry.text}
            </span>
          )}
          <Button
            variant="outline"
            size="sm"
            onClick={() => setConfirmingDisconnect(true)}
            data-statsig-name="settings-computers-disconnect"
          >
            Disconnect
          </Button>
        </div>
      </div>

      {entry.status === "pending" && (
        <div className="flex flex-col gap-2 rounded-md bg-muted/50 p-3">
          <p className="text-sm">
            This computer showed a code when you ran <code>okthx-daemon enroll</code>. Enter it here
            to finish connecting it.
          </p>
          <div className="flex items-center gap-2">
            <Input
              value={code}
              onChange={(e) => {
                setCode(e.target.value);
                confirm.reset();
              }}
              placeholder="XXXXX-XXXXX"
              autoComplete="off"
              spellCheck={false}
              className="max-w-[14rem] font-mono uppercase"
              aria-label="Confirmation code"
            />
            <Button
              size="sm"
              disabled={code.trim() === "" || confirm.isPending}
              onClick={() => confirm.mutate({ machineId: entry.id, confirmationCode: code.trim() })}
              data-statsig-name="settings-computers-confirm"
            >
              {confirm.isPending ? "Connecting..." : "Connect"}
            </Button>
          </div>
          {confirm.isError && (
            <p className="text-sm text-destructive">
              That code didn't work. Codes last 15 minutes and can only be used once — if it has
              lapsed, disconnect this computer and start again.
            </p>
          )}
        </div>
      )}

      <Dialog open={confirmingDisconnect} onOpenChange={setConfirmingDisconnect}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Disconnect {entry.machineName}?</DialogTitle>
            <DialogDescription>
              okthx will stop being able to run commands on it, and the program running there will
              exit. You can connect it again from this page.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex-row justify-end">
            <Button
              variant="outline"
              onClick={() => setConfirmingDisconnect(false)}
              data-statsig-name="settings-computers-disconnect-cancel"
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() =>
                disconnect.mutate(entry.id, { onSuccess: () => setConfirmingDisconnect(false) })
              }
              disabled={disconnect.isPending}
              data-statsig-name="settings-computers-disconnect-confirm"
            >
              {disconnect.isPending ? "Disconnecting..." : "Disconnect"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

/** Whether a minted code can still be redeemed: from when it arrives until it lapses. */
const useCodeStillLive = (minted: MintConnectionCodeResp | null): boolean => {
  const [live, setLive] = useState(false);
  useEffect(() => {
    if (minted === null) {
      setLive(false);
      return;
    }
    setLive(true);
    const lapse = setTimeout(() => setLive(false), minted.expiresInMinutes * 60_000);
    return () => clearTimeout(lapse);
  }, [minted]);
  return live;
};

/**
 * Settings → Computers. The only place a machine is connected from.
 *
 * The assistant cannot reach any of this and never sees either code, which is
 * the point rather than a detail: attaching a machine that runs arbitrary shell
 * is something the user has to do deliberately, at the keyboard of the machine
 * in question.
 */
export const Computers = () => {
  const [consented, setConsented] = useState(false);
  const mint = useMintConnectionCode();
  // Held here and nowhere else. The code is a live capability for fifteen
  // minutes, so it is not put in the query cache, and it goes when this
  // unmounts.
  const [minted, setMinted] = useState<MintConnectionCodeResp | null>(null);
  // The machines on the roster when the code was minted. Enrolling is the only
  // thing that adds a row, so a machine not in this set means the code has
  // been spent: it works once. Taken from a loaded roster only — minting waits
  // for it — so a machine that was already there never reads as new.
  const [machinesAtMint, setMachinesAtMint] = useState<ReadonlySet<number>>(new Set());
  const codeLive = useCodeStillLive(minted);
  // A live, unspent code means the user is on their way to another computer,
  // and the row they come back for is written there rather than here. Once it
  // is spent the pending row keeps the poll going on its own; once it lapses
  // nothing will arrive.
  const [codeSpent, setCodeSpent] = useState(false);
  const { data, isPending, isError, refetch } = useQuery(
    computerQueries.list({ connecting: codeLive && !codeSpent }),
  );
  // One last read as an unspent code lapses: a machine that enrolled since the
  // previous poll would otherwise stay hidden until a reload.
  useEffect(() => {
    if (minted !== null && !codeLive && !codeSpent) void refetch();
  }, [minted, codeLive, codeSpent, refetch]);
  useEffect(() => {
    if (minted === null || !data) return;
    if (data.machines.some((machine) => !machinesAtMint.has(machine.id))) setCodeSpent(true);
  }, [minted, data, machinesAtMint]);
  const onMinted = (resp: MintConnectionCodeResp) => {
    setMinted(resp);
    setMachinesAtMint(new Set(data?.machines.map((machine) => machine.id)));
    setCodeSpent(false);
  };
  // Minting needs the roster as it stands, to know which machine is the new one.
  const rosterLoaded = data !== undefined;

  return (
    <div className="flex flex-col">
      <SettingsPanelHeader title="Computers" />
      <div className="flex flex-col gap-6 px-4 py-4 sm:px-6">
        <section className="flex flex-col gap-3">
          <h3 className="font-medium">Your computers</h3>
          {isPending && <p className="text-sm text-muted-foreground">Loading...</p>}
          {/* A failed read must not render as an empty roster. On a page whose
              job is to show what has shell access to the user's machines,
              "nothing is connected" is the wrong direction to fail in — and the
              gate middleware answers 404, so a gate flip lands here too. */}
          {isError && (
            <p className="text-sm text-destructive">
              Couldn't load your computers. Reload the page to try again.
            </p>
          )}
          {!isPending && !isError && (data?.machines.length ?? 0) === 0 && (
            <p className="text-sm text-muted-foreground">
              No computers are connected. Connect one below to let okthx run commands on it.
            </p>
          )}
          <div className="flex flex-col">
            {data?.machines.map((entry) => (
              <ComputerRow key={entry.id} entry={entry} />
            ))}
          </div>
        </section>

        <section className="flex flex-col gap-3">
          <h3 className="font-medium">Connect a computer</h3>
          <p className="text-sm text-muted-foreground">
            okthx will be able to run shell commands on this computer. Before you connect one:
          </p>
          <ol className="flex list-decimal flex-col gap-2 pl-5 text-sm">
            {CONSEQUENCES.map((line) => (
              <li key={line}>{line}</li>
            ))}
          </ol>
          {/* The honest version for the internal run. The security model's
              wording promises a check on each command before it is sent; that
              check is not built, so this must not claim it. */}
          <div className="rounded-md border border-amber-500/40 bg-amber-500/10 p-3 text-sm">
            Nothing inspects a command before it runs. Treat this as giving okthx the same reach you
            have on that computer — do not connect a machine holding credentials, customer data or
            production access you would not hand it directly.
          </div>

          <div className="flex items-start gap-2">
            <Checkbox
              id="computers-consent"
              checked={consented}
              onCheckedChange={(next) => setConsented(next === true)}
              data-statsig-name="settings-computers-consent"
              className="mt-0.5"
            />
            <Label htmlFor="computers-consent" className="text-sm leading-snug cursor-pointer">
              I understand what connecting a computer gives okthx access to.
            </Label>
          </div>

          {minted === null ? (
            <Button
              className="self-start"
              disabled={!consented || !rosterLoaded || mint.isPending}
              onClick={() => mint.mutate(undefined, { onSuccess: onMinted })}
              data-statsig-name="settings-computers-mint"
            >
              {mint.isPending ? "Creating a code..." : "Create a connection code"}
            </Button>
          ) : (
            <div className="flex flex-col gap-3 rounded-md bg-muted/50 p-3 text-sm">
              <p>On the computer you want to connect, install okthx and run:</p>
              {/* Two copy targets, never one line containing both. A copyable
                  `okthx-daemon enroll <<< "CODE"` would put the code into shell
                  history, which is the whole reason the program reads it from
                  standard input instead of a flag. */}
              <div className="flex items-center gap-2">
                <code className="rounded bg-background px-2 py-1 font-mono">{minted.command}</code>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => void copy(minted.command, "command")}
                  aria-label="Copy the command"
                  data-statsig-name="settings-computers-copy-command"
                >
                  <Copy className="size-4" />
                </Button>
              </div>
              <p>
                It will ask for this code. It lasts {minted.expiresInMinutes} minutes and works
                once:
              </p>
              <div className="flex items-center gap-2">
                <code className="rounded bg-background px-2 py-1 font-mono break-all">
                  {minted.code}
                </code>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => void copy(minted.code, "code")}
                  aria-label="Copy the connection code"
                  data-statsig-name="settings-computers-copy-code"
                >
                  <Copy className="size-4" />
                </Button>
              </div>
              <p className="text-muted-foreground">
                The computer will then show a code of its own. Enter that above to finish — that is
                what proves you are the one sitting at it.
              </p>
              {/* Without this a lapsed code is a dead end: the block never
                  cleared, so the only way to get another was to leave the
                  page and come back. */}
              <Button
                variant="ghost"
                size="sm"
                className="self-start"
                disabled={mint.isPending}
                onClick={() => mint.mutate(undefined, { onSuccess: onMinted })}
                data-statsig-name="settings-computers-remint"
              >
                {mint.isPending ? "Creating a code..." : "Create a new code"}
              </Button>
            </div>
          )}
        </section>
      </div>
    </div>
  );
};
